/** @jsx jsx */
import { jsx } from '@emotion/core';
import useTranslate from 'hooks/useTranslate';
import {
  useMarkdownMultilineTranslate,
  useMarkdownTranslate,
} from 'hooks/useTranslate/useTranslate';
import { phoneMask, zipMask } from 'utils/masks';

import { Typography } from '@material-ui/core';
import { Field, FieldProps, Form, FormikProps } from 'formik';

import PageContainer from 'components/PageContainer';
import { FormTextField, TextFieldPopover } from 'components/TextField';
import { SsnField } from 'components/form';

import ActionButtonContainer from 'components/ActionButtonsContainer';
import Title from 'components/Title';
import CancelButton from 'features/CancelButton';
import DisclosureBox from 'features/DisclosureBox';
import IovationButton from 'features/IovationButton';
import { useRef } from 'react';
import styles from './styles';

export interface FormValues {
  mobilePhone: string;
  ssn: string;
  zip: string;
}

type PropTypes<T extends FormValues> = FormikProps<T>;

export default function MagicLinkInitView<T extends FormValues>(
  props: PropTypes<T>,
) {
  const { isValid, isSubmitting } = props;

  const i18nPrefix = 'magicLink.magicLinkInit.';
  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const mobilePhoneRef = useRef<HTMLElement | null>(null);

  return (
    <Form>
      <PageContainer maxWidth="sm">
        <div css={styles.headingContainer}>
          <Title css={styles.titleText}>
            {tMd('magicLink.magicLinkInit.title')}
          </Title>
          <p css={styles.normalText}>{tMd(`magicLink.magicLinkInit.prompt`)}</p>
        </div>

        <div css={styles.formFields}>
          <Field name="mobilePhone">
            {(fieldProps: FieldProps) => (
              <FormTextField
                id="lookupFormMobilePhone"
                label={t('formLabel.mobilePhone')}
                type="tel"
                autoComplete="tel"
                inputRef={mobilePhoneRef}
                fieldProps={fieldProps}
                mask={phoneMask}
              />
            )}
          </Field>

          <TextFieldPopover id="mobilePhone" textFieldRef={mobilePhoneRef}>
            <Typography variant="body2" component="div">
              {tMdMultiline(`${i18nPrefix}tcpaDisclosure`)}
            </Typography>
          </TextFieldPopover>

          <SsnField />

          <Field name="zip">
            {(fieldProps: FieldProps) => (
              <FormTextField
                id="lookupFormZip"
                label={t('formLabel.zip')}
                autoComplete="postal-code"
                fieldProps={fieldProps}
                mask={zipMask}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            )}
          </Field>
        </div>

        <div css={styles.ccpaDisclosure} data-cy="ccpaDisclosure">
          {tMdMultiline(`${i18nPrefix}ccpaDisclosure`)}
        </div>

        <DisclosureBox css={styles.disclosureBox} type="pre" />

        <div css={styles.finePrint} data-cy="shortCodeDisclosure">
          {tMdMultiline(`${i18nPrefix}shortCodeDisclosure`)}
        </div>

        <ActionButtonContainer>
          <IovationButton
            type="submit"
            id="lookupFormSubmit"
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
          >
            {t(`magicLink.magicLinkInit.nextButton`)}
          </IovationButton>

          <CancelButton>
            {t('magicLink.magicLinkInit.cancelButton')}
          </CancelButton>
        </ActionButtonContainer>
      </PageContainer>
    </Form>
  );
}
